/**
 * Indicador de gente disponivel por jogo.
 *
 * Tres estados visuais distintos de proposito: alguem jogando, ninguem
 * jogando, e nao consegui consultar. Colapsar os dois ultimos num "0"
 * mentiria quando a consulta falha; colapsar num numero qualquer mentiria
 * sempre.
 */
import { useEffect, useState } from 'react';

import type { GameType } from '../lib/adapter.ts';
import { type Presenca, buscarPresenca, presencaDoJogo } from '../lib/presenca.ts';

/** Consulta a presenca e reconsulta enquanto a tela estiver aberta. */
export function usePresenca(stakeMode: 'REAL' | 'VIRTUAL', intervaloMs = 15_000): Presenca {
  const [presenca, setPresenca] = useState<Presenca>({ estado: 'carregando' });

  useEffect(() => {
    let vivo = true;
    const controlador = new AbortController();

    const consultar = async (): Promise<void> => {
      const resultado = await buscarPresenca(stakeMode, controlador.signal);
      if (vivo) setPresenca(resultado);
    };

    void consultar();
    const timer = setInterval(() => void consultar(), intervaloMs);

    return () => {
      vivo = false;
      controlador.abort();
      clearInterval(timer);
    };
  }, [stakeMode, intervaloMs]);

  return presenca;
}

function Ponto({ ativo }: { ativo: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block size-1.5 rounded-full shrink-0"
      style={{
        background: ativo ? 'var(--primary)' : 'var(--muted-foreground)',
        boxShadow: ativo ? '0 0 0 3px color-mix(in oklab, var(--primary) 22%, transparent)' : 'none',
      }}
    />
  );
}

export function PresencaJogoBadge({
  presenca,
  gameType,
}: {
  presenca: Presenca;
  gameType: GameType;
}) {
  if (presenca.estado === 'carregando') {
    return <span className="rotulo opacity-60">consultando…</span>;
  }

  if (presenca.estado === 'demonstracao') {
    return (
      <span className="rotulo opacity-70" title="A demonstração roda só no navegador">
        sem jogadores reais
      </span>
    );
  }

  if (presenca.estado === 'indisponivel') {
    // Nao e zero: e ausencia de informacao, e o texto diz isso.
    return <span className="rotulo opacity-70">contagem indisponível</span>;
  }

  const jogo = presencaDoJogo(presenca, gameType);
  const disponiveis = jogo?.disponiveis ?? 0;

  return (
    <span className="inline-flex items-center gap-1.5 rotulo">
      <Ponto ativo={disponiveis > 0} />
      {disponiveis === 0
        ? 'ninguém agora'
        : `${disponiveis} ${disponiveis === 1 ? 'jogador' : 'jogadores'}`}
    </span>
  );
}

/** Resumo do lobby inteiro, para o topo da tela. */
export function PresencaTotal({ presenca }: { presenca: Presenca }) {
  if (presenca.estado !== 'ok') return null;

  const total = presenca.jogos.reduce((soma, jogo) => soma + jogo.disponiveis, 0);
  const emPartida = presenca.jogos.reduce((soma, jogo) => soma + jogo.emPartida, 0);

  return (
    <p className="rotulo flex items-center gap-1.5">
      <Ponto ativo={total > 0} />
      {total === 0
        ? 'ninguém na mesa agora'
        : `${total} na plataforma · ${emPartida} em partida`}
    </p>
  );
}
